/* eslint-disable react/react-in-jsx-scope */
import { isEmpty, extend } from 'underscore';
import { endsWith } from 'underscore.string';
import Promise from 'bluebird';
import React from 'react';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { AdCustomizerBaseAnalyzer } from '../base-analyzer';

import KeywordEditor from './editor';
import { parseRule, evaluateKeywordRules } from './parser';
import { keywords } from './constants';
import { displayLength } from '../string-utils';

// Replacement text (insert) for when keyword is selected in ad customizer picker
const pickerKeywordInsert = '{KeyWord:}';
const pickerKeywordInsertCursorPosition = pickerKeywordInsert.length - 1;

export class AdCustomizerKeywordAnalyzer extends AdCustomizerBaseAnalyzer {
  constructor({ deps: { i18n, instrumentation }, controller }) {
    super({
      name: 'keyword',
      priority: 1000,
      i18n,
      activityName: 'keyword',
      instrumentation,
      controller,
    });
    this.controller = controller;
    this.defaultAnalyzeResult = parseRule(pickerKeywordInsert, pickerKeywordInsertCursorPosition);
  }

  /* return a reactjs object here */
  getSyntax(analyzeResult) {
    if (!analyzeResult.options) return null;

    const { options: { keywordCase } } = analyzeResult;
    const keywordText = keywords[keywordCase];
    this.syntax = `${this.i18n.getString(_TL_('Syntax:'))} {${keywordText}:${this.i18n.getString(_TL_('default text'))}}`;
    return (
      <div>
        <span
          className="keywordEditor-syntax"
        >{this.syntax}
        </span>
        <HelpPopupInline
          topic="pop_BA_NewAdCreate_KWInsert_Syntax"
          ariaLabel={this.i18n.getString(_TL_('Click to learn more about {{item}}', { item: this.i18n.getString(_TL_('Syntax')) }))}
        />
      </div>);
  }

  needEditor(analyzeResult) {
    const { options } = analyzeResult || {};
    return !isEmpty(options);
  }

  getEditor({
    target, analyzeResult, updateAndCloseCallback,
  }) {
    const { options } = analyzeResult || {};

    return (
      <KeywordEditor
        i18n={this.i18n}
        options={options}
        updateAndCloseCallback={updateAndCloseCallback}
        target={target}
      />);
  }


  // get menu item
  getMenuItem({
    startIndex, endIndex, updateAndCloseCallback, target,
  }) {
    return {
      name: this.i18n.getString(_TL_('Keyword insertion')),
      description: this.i18n.getString(_TL_('Insert the keyword that triggers your ad')),
      onClick: () => {
        const beginning = target.value.substring(0, startIndex);
        const end = target.value.substring(endIndex);
        const newValue = `${beginning}${pickerKeywordInsert}${end}`;
        const suffix = target.value.substring(endIndex);
        this.defaultAnalyzeResult =
          parseRule(newValue, newValue.length - suffix.length - 1);

        updateAndCloseCallback({
          newValue,
          cursorPosition: beginning.length + pickerKeywordInsertCursorPosition,
          isSelectedByUser: true,
        });

        this.controller.updatePicker({
          target,
          syntax: this.getSyntax(this.defaultAnalyzeResult),
          editorView: this.getEditor({
            target,
            analyzeResult: this.defaultAnalyzeResult,
            updateAndCloseCallback,
          }),
          container: this.controller.container,
        });
      },
    };
  }

  analyzeInput({
    text, cursorIndex, updateAndCloseCallback, target,
  }) {
    const rule = parseRule(text, cursorIndex);

    if (rule) {
      const { options: { startIndex } } = rule;
      return Promise.resolve(extend(
        {
          match: true,
          type: this.name,
          defaultHandlerForInput: false,
          itemsToDisplay: [this.getMenuItem({
            startIndex, endIndex: cursorIndex, updateAndCloseCallback, target,
          })],
        },
        rule
      ));
    }
    // when not match, see whether to show menu item
    if (text && endsWith(text, '{', cursorIndex)) {
      return Promise.resolve({
        type: this.name,
        defaultHandlerForInput: false,
        itemsToDisplay: [this.getMenuItem({
          startIndex: text.length - 1,
          endIndex: cursorIndex,
          updateAndCloseCallback,
          target,
        })],
      });
    }
    return Promise.resolve(null);
  }

  evaluateRule(text) {
    return evaluateKeywordRules(text);
  }

  getCharCountExcludingAdCustomizers(text) {
    let charCount = 0;
    let processedText = text || '';
    let toContinue = false;

    let tokens = /\{(?:param[123]|keyword)}/i.exec(processedText);
    if (tokens) {
      processedText = processedText.replace(tokens[0], '');
      toContinue = true;
    } else {
      tokens = /\{(?:param[123]|keyword):([^}]*)}/i.exec(processedText);
      if (tokens) {
        processedText = processedText.replace(tokens[0], '');
        charCount += tokens[1] ? displayLength(tokens[1]) : 0;
        toContinue = true;
      }
    }

    return {
      processedText,
      charCount,
      toContinue,
    };
  }
}
